import React, { useContext, useEffect, useReducer, useState } from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";

import ListItem from "@material-ui/core/ListItem";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import ListItemText from "@material-ui/core/ListItemText";
import { Badge, Collapse, List } from "@material-ui/core";
import DashboardOutlinedIcon from "@material-ui/icons/DashboardOutlined";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import SyncAltIcon from "@material-ui/icons/SyncAlt";
import PeopleAltOutlinedIcon from "@material-ui/icons/PeopleAltOutlined";
import EventAvailableIcon from "@material-ui/icons/EventAvailable";
import LocalAtmIcon from "@material-ui/icons/LocalAtm";
import ForumIcon from "@material-ui/icons/Forum";
import BorderColorIcon from "@material-ui/icons/BorderColor";
import EventIcon from "@material-ui/icons/Event";
import AnnouncementIcon from "@material-ui/icons/Announcement";
import AttachFile from "@material-ui/icons/AttachFile";
import LocalOfferIcon from "@material-ui/icons/LocalOffer";
import HelpOutlineIcon from "@material-ui/icons/HelpOutline";
import AssessmentOutlinedIcon from "@material-ui/icons/AssessmentOutlined";
import { AccountTree } from "@material-ui/icons";
import BusinessCenterIcon from "@material-ui/icons/BusinessCenter";
import TimerIcon from "@material-ui/icons/Timer";
import BarChartIcon from "@material-ui/icons/BarChart";
import MessageIcon from "@material-ui/icons/Message";
import HistoryIcon from "@material-ui/icons/History";
import TuneIcon from "@material-ui/icons/Tune";
import ImportExportIcon from "@material-ui/icons/ImportExport";
import VideoCallIcon from "@material-ui/icons/VideoCall";
import AssignmentIndIcon from "@material-ui/icons/AssignmentInd";
import ExpandLess from "@material-ui/icons/ExpandLess";
import ExpandMore from "@material-ui/icons/ExpandMore";
import ContactPhoneIcon from "@material-ui/icons/ContactPhone";
import ViewKanbanIcon from "@material-ui/icons/ViewColumn";
import GroupIcon from "@material-ui/icons/Group";
import SettingsIcon from "@material-ui/icons/Settings";
import BuildIcon from "@material-ui/icons/Build";
import SupervisorAccountIcon from "@material-ui/icons/SupervisorAccount";
import StorageIcon from "@material-ui/icons/Storage";
import ListAltIcon from "@material-ui/icons/ListAlt";
import MenuBookIcon from "@material-ui/icons/MenuBook";
import StarIcon from "@material-ui/icons/Star";

import { i18n } from "../translate/i18n";
import { WhatsAppsContext } from "../context/WhatsApp/WhatsAppsContext";
import { AuthContext } from "../context/Auth/AuthContext";
import { Can } from "../components/Can";
import { SocketContext } from "../context/Socket/SocketContext";
import { isArray } from "lodash";
import api from "../services/api";
import toastError from "../errors/toastError";
import { makeStyles } from "@material-ui/core/styles";
import usePlans from "../hooks/usePlans";

const SIDEBAR_ACCENT = "#10B981";

const useStyles = makeStyles((theme) => ({
  listItemIcon: {
    color: "#94A3B8",
    minWidth: 40,
  },
  listItemText: {
    color: "#CBD5E1",
    minWidth: 0,
    "&.MuiListItemText-primary": {
      fontWeight: 500,
    },
    "& .MuiTypography-root": {
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
  },
  listItem: {
    minWidth: 0,
    borderRadius: 6,
    margin: "1px 8px",
    width: "auto",
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.06)",
    },
    "&.Mui-selected": {
      backgroundColor: "rgba(16, 185, 129, 0.15)",
      borderLeft: `3px solid ${SIDEBAR_ACCENT}`,
      "& .MuiListItemIcon-root": {
        color: "#94A3B8",
      },
    },
  },
  sectionHeader: {
    fontSize: "0.65rem",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    color: "#475569",
    padding: "14px 16px 4px 16px",
    lineHeight: 1,
    userSelect: "none",
  },
  groupHeader: {
    minWidth: 0,
    borderRadius: 6,
    margin: "1px 8px",
    width: "auto",
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.06)",
    },
  },
  groupHeaderIcon: {
    color: "#94A3B8",
    minWidth: 40,
  },
  groupHeaderText: {
    "& .MuiTypography-root": {
      fontWeight: 600,
      fontSize: "0.875rem",
    },
  },
  nested: {
    paddingLeft: theme.spacing(2),
  },
}));

function ListItemLink(props) {
  const {
    icon,
    primary,
    to,
    listItemClassName,
    listItemIconClassName,
    listItemTextClassName,
    selected,
  } = props;

  const renderLink = React.useMemo(
    () =>
      React.forwardRef((itemProps, ref) => (
        <RouterLink to={to} ref={ref} {...itemProps} />
      )),
    [to]
  );

  return (
    <li>
      <ListItem
        button
        dense
        component={renderLink}
        className={listItemClassName}
        selected={selected}
      >
        {icon ? (
          <ListItemIcon className={listItemIconClassName}>{icon}</ListItemIcon>
        ) : null}
        <ListItemText primary={primary} className={listItemTextClassName} />
      </ListItem>
    </li>
  );
}

const reducer = (state, action) => {
  if (action.type === "LOAD_CHATS") {
    const chats = action.payload;
    const newChats = [];
    if (isArray(chats)) {
      chats.forEach((chat) => {
        const chatIndex = state.findIndex((u) => u.id === chat.id);
        if (chatIndex !== -1) {
          state[chatIndex] = chat;
        } else {
          newChats.push(chat);
        }
      });
    }
    return [...state, ...newChats];
  }
  if (action.type === "UPDATE_CHATS") {
    const chat = action.payload;
    const chatIndex = state.findIndex((u) => u.id === chat.id);
    if (chatIndex !== -1) {
      state[chatIndex] = chat;
      return [...state];
    }
    return [chat, ...state];
  }
  if (action.type === "DELETE_CHAT") {
    const chatId = action.payload;
    const chatIndex = state.findIndex((u) => u.id === chatId);
    if (chatIndex !== -1) {
      state.splice(chatIndex, 1);
    }
    return [...state];
  }
  if (action.type === "RESET") {
    return [];
  }
  if (action.type === "CHANGE_CHAT") {
    return state.map((chat) =>
      chat.id === action.payload.chat.id ? action.payload.chat : chat
    );
  }
};

function defaultAutomacaoPath(flags, isAdmin) {
  if (!isAdmin) return "/quick-messages";
  if (flags.useCampaigns) return "/flowbuilders";
  if (flags.useIntegrations) return "/queue-integration";
  if (flags.useOpenAi) return "/prompts";
  return "/quick-messages";
}

const MainListItems = (props) => {
  const classes = useStyles();
  const { drawerClose } = props;
  const { whatsApps } = useContext(WhatsAppsContext);
  const { user } = useContext(AuthContext);
  const [connectionWarning, setConnectionWarning] = useState(false);
  const [showCampaigns, setShowCampaigns] = useState(false);
  const [showKanban, setShowKanban] = useState(false);
  const [showOpenAi, setShowOpenAi] = useState(false);
  const [showIntegrations, setShowIntegrations] = useState(false);
  const [showSchedules, setShowSchedules] = useState(false);
  const [showExternalApi, setShowExternalApi] = useState(false);

  const [invisible, setInvisible] = useState(true);
  const [pageNumber, setPageNumber] = useState(1);
  const [searchParam] = useState("");
  const [chats, dispatch] = useReducer(reducer, []);
  const { getPlanCompany } = usePlans();
  const location = useLocation();

  // Collapsible group state (persisted in localStorage)
  const stored = (key, fallback) => {
    try { const v = localStorage.getItem("sidebar_" + key); return v !== null ? v === "true" : fallback; } catch { return fallback; }
  };
  const [openAtendimento, setOpenAtendimento] = useState(() => stored("atendimento", true));
  const [openAutomacao, setOpenAutomacao] = useState(() => stored("automacao", false));
  const [openCampanhas, setOpenCampanhas] = useState(() => stored("campanhas", false));
  const [openEquipe, setOpenEquipe] = useState(() => stored("equipe", false));
  const [openGestao, setOpenGestao] = useState(() => stored("gestao", false));
  const [openAdmin, setOpenAdmin] = useState(() => stored("admin", false));

  const toggle = (key, setter) => () => {
    setter((prev) => {
      const next = !prev;
      try { localStorage.setItem("sidebar_" + key, String(next)); } catch {}
      return next;
    });
  };

  const socketManager = useContext(SocketContext);

  const isAdmin = user?.profile === "admin";
  const planFlags = {
    useCampaigns: showCampaigns,
    useKanban: showKanban,
    useOpenAi: showOpenAi,
    useIntegrations: showIntegrations,
    useSchedules: showSchedules,
    useExternalApi: showExternalApi,
  };

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    async function fetchData() {
      const companyId = user.companyId;
      const planConfigs = await getPlanCompany(undefined, companyId);
      setShowCampaigns(planConfigs.plan.useCampaigns);
      setShowKanban(planConfigs.plan.useKanban);
      setShowOpenAi(planConfigs.plan.useOpenAi);
      setShowIntegrations(planConfigs.plan.useIntegrations);
      setShowSchedules(planConfigs.plan.useSchedules);
      setShowExternalApi(planConfigs.plan.useExternalApi);
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchChats();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParam, pageNumber]);

  useEffect(() => {
    const companyId = localStorage.getItem("companyId");
    const socket = socketManager.getSocket(companyId);
    socket.on(`company-${companyId}-chat`, (data) => {
      if (data.action === "new-message" || data.action === "update") {
        dispatch({ type: "CHANGE_CHAT", payload: data });
      }
    });
    return () => {
      socket.disconnect();
    };
  }, [socketManager]);

  useEffect(() => {
    let unreadsCount = 0;
    if (chats.length > 0) {
      for (let chat of chats) {
        for (let chatUser of chat.users) {
          if (Number(chatUser.userId) === Number(user.id)) {
            unreadsCount += chatUser.unreads;
          }
        }
      }
    }
    setInvisible(unreadsCount === 0);
  }, [chats, user.id]);

  useEffect(() => {
    if (localStorage.getItem("cshow")) {
      setShowCampaigns(true);
    }
  }, []);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (whatsApps.length > 0) {
        const offlineWhats = whatsApps.filter(
          (whats) =>
            whats.status === "qrcode" ||
            whats.status === "PAIRING" ||
            whats.status === "DISCONNECTED" ||
            whats.status === "TIMEOUT" ||
            whats.status === "OPENING"
        );
        setConnectionWarning(offlineWhats.length > 0);
      }
    }, 2000);
    return () => clearTimeout(delayDebounceFn);
  }, [whatsApps]);

  const fetchChats = async () => {
    try {
      const { data } = await api.get("/chats/", {
        params: { searchParam, pageNumber },
      });
      dispatch({ type: "LOAD_CHATS", payload: data.records });
    } catch (err) {
      toastError(err);
    }
  };

  const path = location.pathname;

  /* ── selection helpers ── */
  const selDashboard = path === "/" || path === "/relatorios";
  const selTickets = path.startsWith("/tickets");
  const selKanban = path === "/kanban";
  const selContacts = path === "/contacts";
  const selGroups = path === "/group-manager";
  const selAtendimento = selTickets || selKanban || selContacts || selGroups;

  const selFlows = path.startsWith("/flowbuilder") || path === "/flowbuilders";
  const selPhrases = path === "/phrase-lists";
  const selIntegrations = path === "/queue-integration";
  const selPrompts = path === "/prompts";
  const selQuickMsg = path === "/quick-messages";
  const selTemplates = path === "/whatsapp-templates";
  const selAutomacao = selFlows || selPhrases || selIntegrations || selPrompts || selQuickMsg || selTemplates;

  const selCampanhas =
    path === "/campaigns" ||
    path.startsWith("/contact-lists") ||
    path === "/campaigns-config" ||
    path.startsWith("/campaign/");

  const selChatInterno = path.startsWith("/chats");

  const selUsers = path === "/users";
  const selSetores = path === "/setores";
  const selQueues = path === "/queues" || path.startsWith("/queues/");
  const selEquipe = selUsers || selSetores || selQueues;

  const selConfig = path === "/connections" || path === "/messages-api" || path === "/settings" || path === "/companies" || path === "/plans";
  const selFinanceiro = path === "/financeiro";

  const selTarefas = path === "/todolist";
  const selAgendamentos = path === "/schedules";
  const selAvaliacao = path === "/avaliacao";
  const selTags = path === "/tags";
  const selArquivos = path === "/files";
  const selCRM = path === "/crm";
  const selBBB = path === "/bbb-meetings";
  const selImportExport = path === "/contact-import-export";
  const selGestao = selTarefas || selAgendamentos || selAvaliacao || selCSAT || selTags || selArquivos || selCRM || selBBB || selImportExport;

  const selPerformance = path === "/agent-performance";
  const selSLA = path === "/sla-management";
  const selAutoAssign = path === "/auto-assign-rules";
  const selCustomFields = path === "/ticket-custom-fields";
  const selAuditLog = path === "/audit-log";
  const selInformativos = path === "/announcements";
  const selAjuda = path === "/helps";
  const selCSAT = path === "/csat";
  const selKB = path === "/knowledge-base";
  const selAdmin = selPerformance || selSLA || selAutoAssign || selCustomFields || selAuditLog || selInformativos || selAjuda || selKB;

  return (
    <div onClick={drawerClose}>
      {/* ══════════════════════════════════════════
          PRINCIPAL
         ══════════════════════════════════════════ */}
      <div className={classes.sectionHeader}>Principal</div>

      <Can
        role={user.profile}
        perform="dashboard:view"
        yes={() => (
          <ListItemLink
            to="/"
            primary="Dashboard"
            icon={<DashboardOutlinedIcon />}
            listItemClassName={classes.listItem}
            listItemIconClassName={classes.listItemIcon}
            listItemTextClassName={classes.listItemText}
            selected={selDashboard}
          />
        )}
      />

      <ListItemLink
        to="/chats"
        primary={i18n.t("mainDrawer.sections.chatInterno")}
        icon={
          <Badge color="secondary" variant="dot" invisible={invisible}>
            <ForumIcon />
          </Badge>
        }
        listItemClassName={classes.listItem}
        listItemIconClassName={classes.listItemIcon}
        listItemTextClassName={classes.listItemText}
        selected={selChatInterno}
      />

      {/* ══════════════════════════════════════════
          ATENDIMENTO (collapsible)
         ══════════════════════════════════════════ */}
      <div className={classes.sectionHeader}>Atendimento</div>

      <ListItem button dense className={classes.groupHeader} onClick={toggle("atendimento", setOpenAtendimento)}>
        <ListItemIcon className={classes.groupHeaderIcon}><WhatsAppIcon /></ListItemIcon>
        <ListItemText primary="Tickets & Contatos" className={classes.groupHeaderText} />
        {openAtendimento ? <ExpandLess style={{ color: "rgba(0,0,0,0.4)" }} /> : <ExpandMore style={{ color: "rgba(0,0,0,0.4)" }} />}
      </ListItem>
      <Collapse in={openAtendimento} timeout="auto" unmountOnExit>
        <List component="div" disablePadding className={classes.nested}>
          <ListItemLink
            to="/tickets"
            primary="Tickets"
            icon={<WhatsAppIcon />}
            listItemClassName={classes.listItem}
            listItemIconClassName={classes.listItemIcon}
            listItemTextClassName={classes.listItemText}
            selected={selTickets}
          />
          {showKanban && (
            <ListItemLink
              to="/kanban"
              primary="Kanban"
              icon={<ViewKanbanIcon />}
              listItemClassName={classes.listItem}
              listItemIconClassName={classes.listItemIcon}
              listItemTextClassName={classes.listItemText}
              selected={selKanban}
            />
          )}
          <ListItemLink
            to="/contacts"
            primary="Contatos"
            icon={<ContactPhoneIcon />}
            listItemClassName={classes.listItem}
            listItemIconClassName={classes.listItemIcon}
            listItemTextClassName={classes.listItemText}
            selected={selContacts}
          />
          <ListItemLink
            to="/group-manager"
            primary="Grupos"
            icon={<GroupIcon />}
            listItemClassName={classes.listItem}
            listItemIconClassName={classes.listItemIcon}
            listItemTextClassName={classes.listItemText}
            selected={selGroups}
          />
          <ListItemLink
            to="/tags"
            primary="Tags"
            icon={<LocalOfferIcon />}
            listItemClassName={classes.listItem}
            listItemIconClassName={classes.listItemIcon}
            listItemTextClassName={classes.listItemText}
            selected={selTags}
          />
          <ListItemLink
            to="/contact-import-export"
            primary="Importar / Exportar"
            icon={<ImportExportIcon />}
            listItemClassName={classes.listItem}
            listItemIconClassName={classes.listItemIcon}
            listItemTextClassName={classes.listItemText}
            selected={selImportExport}
          />
        </List>
      </Collapse>

      {/* ══════════════════════════════════════════
          AUTOMAÇÃO (collapsible)
         ══════════════════════════════════════════ */}
      <ListItem button dense className={classes.groupHeader} onClick={toggle("automacao", setOpenAutomacao)}>
        <ListItemIcon className={classes.groupHeaderIcon}><AccountTree /></ListItemIcon>
        <ListItemText primary="Automação" className={classes.groupHeaderText} />
        {openAutomacao ? <ExpandLess style={{ color: "rgba(0,0,0,0.4)" }} /> : <ExpandMore style={{ color: "rgba(0,0,0,0.4)" }} />}
      </ListItem>
      <Collapse in={openAutomacao} timeout="auto" unmountOnExit>
        <List component="div" disablePadding className={classes.nested}>
          <ListItemLink
            to="/quick-messages"
            primary="Respostas Rápidas"
            icon={<MessageIcon />}
            listItemClassName={classes.listItem}
            listItemIconClassName={classes.listItemIcon}
            listItemTextClassName={classes.listItemText}
            selected={selQuickMsg}
          />
          {isAdmin && (
            <>
              <ListItemLink
                to="/flowbuilders"
                primary="Fluxos (Chatbot)"
                icon={<AccountTree />}
                listItemClassName={classes.listItem}
                listItemIconClassName={classes.listItemIcon}
                listItemTextClassName={classes.listItemText}
                selected={selFlows}
              />
              <ListItemLink
                to="/phrase-lists"
                primary="Palavras-Chave"
                icon={<ListAltIcon />}
                listItemClassName={classes.listItem}
                listItemIconClassName={classes.listItemIcon}
                listItemTextClassName={classes.listItemText}
                selected={selPhrases}
              />
              {showIntegrations && (
                <ListItemLink
                  to="/queue-integration"
                  primary="Integrações"
                  icon={<StorageIcon />}
                  listItemClassName={classes.listItem}
                  listItemIconClassName={classes.listItemIcon}
                  listItemTextClassName={classes.listItemText}
                  selected={selIntegrations}
                />
              )}
              {showOpenAi && (
                <ListItemLink
                  to="/prompts"
                  primary="Prompts IA"
                  icon={<BuildIcon />}
                  listItemClassName={classes.listItem}
                  listItemIconClassName={classes.listItemIcon}
                  listItemTextClassName={classes.listItemText}
                  selected={selPrompts}
                />
              )}
            </>
          )}
          <ListItemLink
            to="/whatsapp-templates"
            primary="Templates WhatsApp"
            icon={<MessageIcon />}
            listItemClassName={classes.listItem}
            listItemIconClassName={classes.listItemIcon}
            listItemTextClassName={classes.listItemText}
            selected={selTemplates}
          />
        </List>
      </Collapse>

      {/* ══════════════════════════════════════════
          CAMPANHAS (collapsible, admin only)
         ══════════════════════════════════════════ */}
      {isAdmin && showCampaigns && (
        <>
          <ListItem button dense className={classes.groupHeader} onClick={toggle("campanhas", setOpenCampanhas)}>
            <ListItemIcon className={classes.groupHeaderIcon}><EventAvailableIcon /></ListItemIcon>
            <ListItemText primary="Campanhas" className={classes.groupHeaderText} />
            {openCampanhas ? <ExpandLess style={{ color: "rgba(0,0,0,0.4)" }} /> : <ExpandMore style={{ color: "rgba(0,0,0,0.4)" }} />}
          </ListItem>
          <Collapse in={openCampanhas} timeout="auto" unmountOnExit>
            <List component="div" disablePadding className={classes.nested}>
              <ListItemLink
                to="/campaigns"
                primary="Campanhas"
                icon={<EventAvailableIcon />}
                listItemClassName={classes.listItem}
                listItemIconClassName={classes.listItemIcon}
                listItemTextClassName={classes.listItemText}
                selected={path === "/campaigns"}
              />
              <ListItemLink
                to="/contact-lists"
                primary="Listas de Contatos"
                icon={<ContactPhoneIcon />}
                listItemClassName={classes.listItem}
                listItemIconClassName={classes.listItemIcon}
                listItemTextClassName={classes.listItemText}
                selected={path.startsWith("/contact-lists")}
              />
              <ListItemLink
                to="/campaigns-config"
                primary="Configurações"
                icon={<SettingsIcon />}
                listItemClassName={classes.listItem}
                listItemIconClassName={classes.listItemIcon}
                listItemTextClassName={classes.listItemText}
                selected={path === "/campaigns-config"}
              />
            </List>
          </Collapse>
        </>
      )}

      {/* ══════════════════════════════════════════
          GESTÃO (collapsible)
         ══════════════════════════════════════════ */}
      <div className={classes.sectionHeader}>Gestão</div>

      <ListItem button dense className={classes.groupHeader} onClick={toggle("gestao", setOpenGestao)}>
        <ListItemIcon className={classes.groupHeaderIcon}><BusinessCenterIcon /></ListItemIcon>
        <ListItemText primary="Produtividade" className={classes.groupHeaderText} />
        {openGestao ? <ExpandLess style={{ color: "rgba(0,0,0,0.4)" }} /> : <ExpandMore style={{ color: "rgba(0,0,0,0.4)" }} />}
      </ListItem>
      <Collapse in={openGestao} timeout="auto" unmountOnExit>
        <List component="div" disablePadding className={classes.nested}>
          <ListItemLink
            to="/crm"
            primary="CRM"
            icon={<BusinessCenterIcon />}
            listItemClassName={classes.listItem}
            listItemIconClassName={classes.listItemIcon}
            listItemTextClassName={classes.listItemText}
            selected={selCRM}
          />
          <ListItemLink
            to="/todolist"
            primary="Tarefas"
            icon={<BorderColorIcon />}
            listItemClassName={classes.listItem}
            listItemIconClassName={classes.listItemIcon}
            listItemTextClassName={classes.listItemText}
            selected={selTarefas}
          />
          {showSchedules && (
            <ListItemLink
              to="/schedules"
              primary="Agendamentos"
              icon={<EventIcon />}
              listItemClassName={classes.listItem}
              listItemIconClassName={classes.listItemIcon}
              listItemTextClassName={classes.listItemText}
              selected={selAgendamentos}
            />
          )}
          <ListItemLink
            to="/bbb-meetings"
            primary="Reuniões"
            icon={<VideoCallIcon />}
            listItemClassName={classes.listItem}
            listItemIconClassName={classes.listItemIcon}
            listItemTextClassName={classes.listItemText}
            selected={selBBB}
          />
          <ListItemLink
            to="/avaliacao"
            primary="Avaliações"
            icon={<AssessmentOutlinedIcon />}
            listItemClassName={classes.listItem}
            listItemIconClassName={classes.listItemIcon}
            listItemTextClassName={classes.listItemText}
            selected={selAvaliacao}
          />
          <ListItemLink
            to="/csat"
            primary="Satisfação (CSAT)"
            icon={<StarIcon />}
            listItemClassName={classes.listItem}
            listItemIconClassName={classes.listItemIcon}
            listItemTextClassName={classes.listItemText}
            selected={selCSAT}
          />
          <ListItemLink
            to="/files"
            primary="Arquivos"
            icon={<AttachFile />}
            listItemClassName={classes.listItem}
            listItemIconClassName={classes.listItemIcon}
            listItemTextClassName={classes.listItemText}
            selected={selArquivos}
          />
        </List>
      </Collapse>

      {/* ══════════════════════════════════════════
          EQUIPE (collapsible, admin)
         ══════════════════════════════════════════ */}
      {isAdmin && (
        <>
          <ListItem button dense className={classes.groupHeader} onClick={toggle("equipe", setOpenEquipe)}>
            <ListItemIcon className={classes.groupHeaderIcon}><PeopleAltOutlinedIcon /></ListItemIcon>
            <ListItemText primary="Equipe" className={classes.groupHeaderText} />
            {openEquipe ? <ExpandLess style={{ color: "rgba(0,0,0,0.4)" }} /> : <ExpandMore style={{ color: "rgba(0,0,0,0.4)" }} />}
          </ListItem>
          <Collapse in={openEquipe} timeout="auto" unmountOnExit>
            <List component="div" disablePadding className={classes.nested}>
              <ListItemLink
                to="/users"
                primary="Usuários"
                icon={<PeopleAltOutlinedIcon />}
                listItemClassName={classes.listItem}
                listItemIconClassName={classes.listItemIcon}
                listItemTextClassName={classes.listItemText}
                selected={selUsers}
              />
              <ListItemLink
                to="/setores"
                primary="Setores"
                icon={<SupervisorAccountIcon />}
                listItemClassName={classes.listItem}
                listItemIconClassName={classes.listItemIcon}
                listItemTextClassName={classes.listItemText}
                selected={selSetores}
              />
              <ListItemLink
                to="/queues"
                primary="Filas"
                icon={<ListAltIcon />}
                listItemClassName={classes.listItem}
                listItemIconClassName={classes.listItemIcon}
                listItemTextClassName={classes.listItemText}
                selected={selQueues}
              />
              <ListItemLink
                to="/agent-performance"
                primary="Desempenho"
                icon={<BarChartIcon />}
                listItemClassName={classes.listItem}
                listItemIconClassName={classes.listItemIcon}
                listItemTextClassName={classes.listItemText}
                selected={selPerformance}
              />
              <ListItemLink
                to="/sla-management"
                primary="Políticas SLA"
                icon={<TimerIcon />}
                listItemClassName={classes.listItem}
                listItemIconClassName={classes.listItemIcon}
                listItemTextClassName={classes.listItemText}
                selected={selSLA}
              />
              <ListItemLink
                to="/auto-assign-rules"
                primary="Auto-Atribuição"
                icon={<AssignmentIndIcon />}
                listItemClassName={classes.listItem}
                listItemIconClassName={classes.listItemIcon}
                listItemTextClassName={classes.listItemText}
                selected={selAutoAssign}
              />
            </List>
          </Collapse>
        </>
      )}

      {/* ══════════════════════════════════════════
          ADMINISTRAÇÃO
         ══════════════════════════════════════════ */}
      {isAdmin && (
        <>
          <div className={classes.sectionHeader}>Administração</div>

          <ListItemLink
            to="/financeiro"
            primary="Financeiro"
            icon={<LocalAtmIcon />}
            listItemClassName={classes.listItem}
            listItemIconClassName={classes.listItemIcon}
            listItemTextClassName={classes.listItemText}
            selected={selFinanceiro}
          />

          <ListItem button dense className={classes.groupHeader} onClick={toggle("admin", setOpenAdmin)}>
            <ListItemIcon className={classes.groupHeaderIcon}><SettingsIcon /></ListItemIcon>
            <ListItemText primary="Configurações" className={classes.groupHeaderText} />
            {openAdmin ? <ExpandLess style={{ color: "rgba(0,0,0,0.4)" }} /> : <ExpandMore style={{ color: "rgba(0,0,0,0.4)" }} />}
          </ListItem>
          <Collapse in={openAdmin} timeout="auto" unmountOnExit>
            <List component="div" disablePadding className={classes.nested}>
              <ListItemLink
                to="/connections"
                primary="Conexões"
                icon={
                  <Badge badgeContent={connectionWarning ? "!" : 0} color="error">
                    <SyncAltIcon />
                  </Badge>
                }
                listItemClassName={classes.listItem}
                listItemIconClassName={classes.listItemIcon}
                listItemTextClassName={classes.listItemText}
                selected={path === "/connections"}
              />
              {showExternalApi && (
                <ListItemLink
                  to="/messages-api"
                  primary="API de Mensagens"
                  icon={<StorageIcon />}
                  listItemClassName={classes.listItem}
                  listItemIconClassName={classes.listItemIcon}
                  listItemTextClassName={classes.listItemText}
                  selected={path === "/messages-api"}
                />
              )}
              <ListItemLink
                to="/ticket-custom-fields"
                primary="Campos Personalizados"
                icon={<TuneIcon />}
                listItemClassName={classes.listItem}
                listItemIconClassName={classes.listItemIcon}
                listItemTextClassName={classes.listItemText}
                selected={selCustomFields}
              />
              <ListItemLink
                to="/audit-log"
                primary="Log de Auditoria"
                icon={<HistoryIcon />}
                listItemClassName={classes.listItem}
                listItemIconClassName={classes.listItemIcon}
                listItemTextClassName={classes.listItemText}
                selected={selAuditLog}
              />
              <ListItemLink
                to="/settings"
                primary="Configurações Gerais"
                icon={<SettingsIcon />}
                listItemClassName={classes.listItem}
                listItemIconClassName={classes.listItemIcon}
                listItemTextClassName={classes.listItemText}
                selected={path === "/settings"}
              />
              {user.super && (
                <>
                  <ListItemLink
                    to="/announcements"
                    primary="Informativos"
                    icon={<AnnouncementIcon />}
                    listItemClassName={classes.listItem}
                    listItemIconClassName={classes.listItemIcon}
                    listItemTextClassName={classes.listItemText}
                    selected={selInformativos}
                  />
                  <ListItemLink
                    to="/companies"
                    primary="Empresas"
                    icon={<SupervisorAccountIcon />}
                    listItemClassName={classes.listItem}
                    listItemIconClassName={classes.listItemIcon}
                    listItemTextClassName={classes.listItemText}
                    selected={path === "/companies"}
                  />
                  <ListItemLink
                    to="/plans"
                    primary="Planos"
                    icon={<LocalAtmIcon />}
                    listItemClassName={classes.listItem}
                    listItemIconClassName={classes.listItemIcon}
                    listItemTextClassName={classes.listItemText}
                    selected={path === "/plans"}
                  />
                </>
              )}
            </List>
          </Collapse>
        </>
      )}

      {/* ══════════════════════════════════════════
          AJUDA (always visible)
         ══════════════════════════════════════════ */}
      <div className={classes.sectionHeader} style={{ marginTop: 8 }}>Suporte</div>
      <ListItemLink
        to="/helps"
        primary="Central de Ajuda"
        icon={<HelpOutlineIcon />}
        listItemClassName={classes.listItem}
        listItemIconClassName={classes.listItemIcon}
        listItemTextClassName={classes.listItemText}
        selected={selAjuda}
      />
      <ListItemLink
        to="/knowledge-base"
        primary="Base de Conhecimento"
        icon={<MenuBookIcon />}
        listItemClassName={classes.listItem}
        listItemIconClassName={classes.listItemIcon}
        listItemTextClassName={classes.listItemText}
        selected={selKB}
      />
    </div>
  );
};

export default MainListItems;
